import type { CSSProperties } from 'react'
import type { ForecastMonthMetric } from '@/api/types'
import { Money } from '@/components/Money'
import { formatMoney, formatMoneyCompact } from '@/lib/format-money'
import { MONTH_METRIC_OPTIONS, MONTH_STRIP_LABELS } from '@/lib/month-strip'
import {
  formatMomChangeLabel,
  monthOverMonthChangeRatio,
  monthOverMonthTintStyle,
} from '@/lib/month-over-month-tint'

interface MonthStripProps {
  amounts: number[]
  /** Defaults to Jan–Dec; pass a subset when a quarter filter is active. */
  labels?: readonly string[]
  currency?: string
  density?: 'default' | 'compact'
  className?: string
  'aria-label'?: string
}

/** Read-only month money strip with MoM background tint vs prior month. */
export function MonthStrip({
  amounts,
  labels = MONTH_STRIP_LABELS,
  currency = 'USD',
  density = 'default',
  className = '',
  'aria-label': ariaLabel = 'Monthly amounts',
}: MonthStripProps) {
  return (
    <div
      className={`month-strip month-strip--${density}${className ? ` ${className}` : ''}`}
      aria-label={ariaLabel}
    >
      {labels.map((label, index) => {
        const amount = amounts[index] ?? 0
        const previous = index === 0 ? null : (amounts[index - 1] ?? 0)
        const changeRatio = monthOverMonthChangeRatio(amount, previous)
        const tintStyle = monthOverMonthTintStyle(changeRatio)
        const momLabel = formatMomChangeLabel(changeRatio)
        const active = amount > 0
        const style: CSSProperties | undefined = tintStyle
        const title = [formatMoney(amount, currency), momLabel].filter(Boolean).join(' · ')

        return (
          <div
            key={`${label}-${index}`}
            className={`month-strip__cell${active ? ' month-strip__cell--active' : ''}${
              tintStyle ? ' month-strip__cell--tinted' : ''
            }`}
            style={style}
            title={title}
          >
            <span className="month-strip__label">{label}</span>
            <span className="month-strip__value">
              {density === 'compact' ? (
                formatMoneyCompact(amount, currency)
              ) : (
                <Money amount={amount} currency={currency} />
              )}
            </span>
          </div>
        )
      })}
    </div>
  )
}

interface MonthMetricToggleProps {
  value: ForecastMonthMetric
  onChange: (metric: ForecastMonthMetric) => void
  idPrefix?: string
}

export function MonthMetricToggle({
  value,
  onChange,
  idPrefix = 'month-metric',
}: MonthMetricToggleProps) {
  return (
    <div className="segmented month-strip__metric" role="group" aria-label="Month metric">
      {MONTH_METRIC_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          id={`${idPrefix}-${option.value}`}
          className={`segmented__item${value === option.value ? ' segmented__item--active' : ''}`}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
